import { useState, useEffect, useCallback } from 'react';
import {
  Shield, FileText, Building2, Users, ClipboardCheck, Search,
  Loader2, ExternalLink, AlertTriangle, CheckCircle2, Clock, MinusCircle,
  RefreshCw, Plus,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPatch, apiDelete } from '../lib/api';

const TAB_KEYS = ['audit_log', 'vendors', 'access_review', 'checklist'];
const TAB_LABELS = {
  audit_log:     { label: 'Audit Log',         icon: FileText },
  vendors:       { label: 'Vendor Inventory',  icon: Building2 },
  access_review: { label: 'Access Review',     icon: Users },
  checklist:     { label: 'Evidence Checklist', icon: ClipboardCheck },
};

const RISK_STYLES = {
  critical: 'bg-red-100 text-red-800 border-red-300',
  high:     'bg-orange-100 text-orange-800 border-orange-300',
  medium:   'bg-amber-100 text-amber-800 border-amber-300',
  low:      'bg-emerald-100 text-emerald-800 border-emerald-300',
};

const STATUS_STYLES = {
  complete:    { bg: 'bg-emerald-100', text: 'text-emerald-800', icon: CheckCircle2, label: 'Complete' },
  in_progress: { bg: 'bg-amber-100',   text: 'text-amber-800',   icon: Clock,         label: 'In Progress' },
  not_started: { bg: 'bg-slate-100',   text: 'text-slate-700',   icon: AlertTriangle, label: 'Not Started' },
  n_a:         { bg: 'bg-slate-50',    text: 'text-slate-500',   icon: MinusCircle,   label: 'N/A' },
};

const SOC2_PILL = {
  certified:   'bg-emerald-50 text-emerald-700 border-emerald-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  none:        'bg-red-50 text-red-700 border-red-200',
  unknown:     'bg-slate-50 text-slate-600 border-slate-200',
};


// ─── AUDIT LOG TAB ─────────────────────────────────────────────────────────────
function AuditLogTab() {
  const [events, setEvents] = useState([]);
  const [totalMatching, setTotalMatching] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ event_type: '', user_id: '', success: '' });
  const [eventTypes, setEventTypes] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (filter.event_type) q.set('event_type', filter.event_type);
    if (filter.user_id) q.set('user_id', filter.user_id);
    if (filter.success !== '') q.set('success', filter.success);
    q.set('limit', '200');
    try {
      const res = await apiGet(`/api/compliance/audit-log?${q.toString()}`);
      const data = await res.json();
      if (res.ok) {
        setEvents(data.events || []);
        setTotalMatching(data.total_matching || 0);
      }
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    apiGet('/api/compliance/audit-log/event-types').then(r => r.json()).then(d => setEventTypes(d.event_types || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const headers = ['timestamp', 'event_type', 'user_id', 'success', 'ip', 'target_type', 'target_id', 'details'];
    const rows = events.map(e => [
      e.timestamp, e.event_type, e.user_id || '', e.success,
      e.ip || '', e.target_type || '', e.target_id || '',
      JSON.stringify(e.details || {}),
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success('Audit log exported');
  };

  return (
    <div className="space-y-4" data-testid="audit-log-tab">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs">Event Type</Label>
          <select
            className="w-full h-9 px-2 border border-border-subtle rounded-md text-sm bg-white"
            value={filter.event_type}
            onChange={(e) => setFilter({ ...filter, event_type: e.target.value })}
            data-testid="audit-filter-event-type"
          >
            <option value="">All</option>
            {eventTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs">User ID</Label>
          <Input value={filter.user_id} onChange={(e) => setFilter({ ...filter, user_id: e.target.value })}
                 placeholder="exact user id" data-testid="audit-filter-user" />
        </div>
        <div className="min-w-[120px]">
          <Label className="text-xs">Status</Label>
          <select
            className="w-full h-9 px-2 border border-border-subtle rounded-md text-sm bg-white"
            value={filter.success}
            onChange={(e) => setFilter({ ...filter, success: e.target.value })}
            data-testid="audit-filter-status"
          >
            <option value="">All</option>
            <option value="true">Success</option>
            <option value="false">Failed</option>
          </select>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="audit-refresh">
          <RefreshCw size={14} className="mr-1.5" /> Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={!events.length} data-testid="audit-export-csv">
          Export CSV
        </Button>
      </div>

      <div className="text-xs text-text-muted">
        Showing {events.length} of {totalMatching} matching events
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-brand" /></div>
      ) : (
        <div className="overflow-x-auto border border-border-subtle rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-surface-page">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Timestamp</th>
                <th className="text-left px-3 py-2 font-semibold">Event</th>
                <th className="text-left px-3 py-2 font-semibold">User</th>
                <th className="text-left px-3 py-2 font-semibold">IP</th>
                <th className="text-left px-3 py-2 font-semibold">Target</th>
                <th className="text-left px-3 py-2 font-semibold">Status</th>
                <th className="text-left px-3 py-2 font-semibold">Details</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-text-muted">No events match these filters</td></tr>
              ) : events.map(e => (
                <tr key={e.id} className="border-t border-border-subtle hover:bg-surface-page/50">
                  <td className="px-3 py-2 font-mono text-xs">{e.timestamp?.replace('T', ' ').slice(0, 19)}</td>
                  <td className="px-3 py-2 font-medium">{e.event_type}</td>
                  <td className="px-3 py-2 font-mono text-xs">{(e.user_id || '—').slice(0, 12)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{e.ip || '—'}</td>
                  <td className="px-3 py-2">{e.target_type ? `${e.target_type}:${(e.target_id || '').slice(0, 8)}` : '—'}</td>
                  <td className="px-3 py-2">
                    {e.success
                      ? <span className="text-emerald-700">✓</span>
                      : <span className="text-red-700">✗</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-text-muted max-w-[280px] truncate">
                    {Object.keys(e.details || {}).length ? JSON.stringify(e.details) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ─── VENDORS TAB ───────────────────────────────────────────────────────────────
function VendorsTab() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiGet('/api/compliance/vendors');
      const d = await r.json();
      if (r.ok) setVendors(d.vendors || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (vendor) => {
    try {
      const isNew = !vendor.id;
      const path = isNew ? '/api/compliance/vendors' : `/api/compliance/vendors/${vendor.id}`;
      const fn = isNew ? apiPost : apiPatch;
      const payload = { ...vendor }; delete payload.id; delete payload.created_at; delete payload.updated_at;
      const r = await fn(path, payload);
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail || 'Save failed'); }
      toast.success(isNew ? 'Vendor added' : 'Vendor updated');
      setEditing(null);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (vendor) => {
    if (!window.confirm(`Delete vendor "${vendor.name}"?`)) return;
    try {
      const r = await apiDelete(`/api/compliance/vendors/${vendor.id}`);
      if (!r.ok) throw new Error('Delete failed');
      toast.success('Vendor deleted');
      load();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-brand" /></div>;

  return (
    <div className="space-y-4" data-testid="vendors-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-text-muted">
          {vendors.length} vendors · review quarterly · evidence for CC3.2 + SOC 2 vendor management
        </p>
        <Button size="sm" onClick={() => setEditing({ name: '', category: '', data_handled: '', risk_tier: 'medium', soc2_status: 'unknown', url: '' })} data-testid="vendor-add">
          <Plus size={14} className="mr-1.5" /> Add Vendor
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {vendors.map(v => (
          <Card key={v.id} className="border-border-subtle" data-testid={`vendor-${v.id}`}>
            <CardContent className="pt-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-text-primary">{v.name}</p>
                  <p className="text-xs text-text-muted">{v.category}</p>
                </div>
                <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded border ${RISK_STYLES[v.risk_tier] || RISK_STYLES.medium}`}>
                  {v.risk_tier}
                </span>
              </div>
              <p className="text-sm text-text-secondary">{v.data_handled}</p>
              <div className="flex flex-wrap items-center gap-2 pt-1 text-xs">
                <span className={`px-2 py-0.5 rounded border ${SOC2_PILL[v.soc2_status]}`}>SOC 2: {v.soc2_status.replace('_', ' ')}</span>
                {v.last_reviewed && <span className="text-text-muted">reviewed {v.last_reviewed}</span>}
                {v.url && <a href={v.url} target="_blank" rel="noopener noreferrer" className="text-brand hover:underline inline-flex items-center gap-1"><ExternalLink size={11} /> docs</a>}
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setEditing(v)} data-testid={`vendor-edit-${v.id}`}>Edit</Button>
                <Button variant="outline" size="sm" onClick={() => remove(v)} className="text-red-700" data-testid={`vendor-delete-${v.id}`}>Delete</Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditing(null)} data-testid="vendor-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold">{editing.id ? 'Edit Vendor' : 'Add Vendor'}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} /></div>
              <div><Label>Category *</Label><Input value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value })} placeholder="LLM / Hosting / Auth / ..." /></div>
            </div>
            <div><Label>Data handled *</Label><Textarea rows={2} value={editing.data_handled} onChange={e => setEditing({ ...editing, data_handled: e.target.value })} placeholder="What data flows to this vendor?" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Risk Tier *</Label>
                <select className="w-full h-9 px-2 border border-border-subtle rounded-md text-sm bg-white" value={editing.risk_tier} onChange={e => setEditing({ ...editing, risk_tier: e.target.value })}>
                  <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                </select>
              </div>
              <div>
                <Label>SOC 2 Status *</Label>
                <select className="w-full h-9 px-2 border border-border-subtle rounded-md text-sm bg-white" value={editing.soc2_status} onChange={e => setEditing({ ...editing, soc2_status: e.target.value })}>
                  <option value="certified">Certified</option><option value="in_progress">In Progress</option><option value="none">None</option><option value="unknown">Unknown</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Last Reviewed</Label><Input value={editing.last_reviewed || ''} onChange={e => setEditing({ ...editing, last_reviewed: e.target.value })} placeholder="YYYY-MM-DD" /></div>
              <div><Label>Vendor URL</Label><Input value={editing.url || ''} onChange={e => setEditing({ ...editing, url: e.target.value })} placeholder="https://..." /></div>
            </div>
            <div><Label>Review Notes</Label><Textarea rows={2} value={editing.review_notes || ''} onChange={e => setEditing({ ...editing, review_notes: e.target.value })} /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={() => save(editing)} data-testid="vendor-save">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── ACCESS REVIEW TAB ─────────────────────────────────────────────────────────
function AccessReviewTab() {
  const [data, setData] = useState({ users: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    apiGet('/api/compliance/access-review')
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-brand" /></div>;

  const filtered = data.users.filter(u =>
    !search ||
    (u.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.role || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="access-review-tab">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-text-muted">{data.count} users · evidence for CC5.1 quarterly access review</p>
        <div className="relative w-64">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search users..." className="pl-7" data-testid="access-review-search" />
        </div>
      </div>

      <div className="overflow-x-auto border border-border-subtle rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-surface-page">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">User</th>
              <th className="text-left px-3 py-2 font-semibold">Role</th>
              <th className="text-left px-3 py-2 font-semibold">Active</th>
              <th className="text-left px-3 py-2 font-semibold">Drive Folders</th>
              <th className="text-left px-3 py-2 font-semibold">Integrations</th>
              <th className="text-left px-3 py-2 font-semibold">Last Login</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-text-muted">No users match this search</td></tr>
            ) : filtered.map(u => (
              <tr key={u.user_id} className="border-t border-border-subtle hover:bg-surface-page/50">
                <td className="px-3 py-2">
                  <p className="font-medium">{u.name || '—'}</p>
                  <p className="text-xs text-text-muted">{u.email}</p>
                </td>
                <td className="px-3 py-2">
                  <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${u.role === 'Admin' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>
                    {u.role}
                  </span>
                </td>
                <td className="px-3 py-2">{u.is_active ? <span className="text-emerald-700">✓</span> : <span className="text-red-700">✗</span>}</td>
                <td className="px-3 py-2 text-xs">{(u.assigned_folders || []).length} folders</td>
                <td className="px-3 py-2 text-xs">
                  {(u.integrations || []).length === 0 ? '—' :
                    (u.integrations || []).map(i => `${i.provider}:${i.account_name || i.account_id}`).join(', ')}
                </td>
                <td className="px-3 py-2 text-xs font-mono">{u.last_login ? u.last_login.slice(0, 10) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ─── CHECKLIST TAB ─────────────────────────────────────────────────────────────
function ChecklistTab() {
  const [data, setData] = useState({ items: [], summary: {} });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiGet('/api/compliance/checklist');
      const d = await r.json();
      if (r.ok) setData(d);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (item, status) => {
    try {
      const r = await apiPatch(`/api/compliance/checklist/${item.id}`, { status, note: item.note });
      if (!r.ok) throw new Error('Update failed');
      load();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-brand" /></div>;

  const grouped = data.items.reduce((acc, item) => {
    (acc[item.criterion] = acc[item.criterion] || []).push(item);
    return acc;
  }, {});

  const sum = data.summary;
  const pct = sum.total ? Math.round((sum.complete / sum.total) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="checklist-tab">
      <div className="grid grid-cols-4 gap-3">
        <Card><CardContent className="pt-4"><p className="text-xs text-text-muted uppercase">Complete</p><p className="text-2xl font-bold text-emerald-700">{sum.complete}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-xs text-text-muted uppercase">In Progress</p><p className="text-2xl font-bold text-amber-700">{sum.in_progress}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-xs text-text-muted uppercase">Not Started</p><p className="text-2xl font-bold text-slate-700">{sum.not_started}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-xs text-text-muted uppercase">Overall</p><p className="text-2xl font-bold text-text-primary">{pct}%</p></CardContent></Card>
      </div>

      {Object.entries(grouped).map(([crit, items]) => (
        <div key={crit}>
          <h3 className="text-sm font-bold uppercase tracking-wider text-text-muted mb-2">{crit}</h3>
          <div className="space-y-2">
            {items.map(item => {
              const cfg = STATUS_STYLES[item.status] || STATUS_STYLES.not_started;
              const Icon = cfg.icon;
              return (
                <div key={item.id} className="flex items-start gap-3 p-3 border border-border-subtle rounded-md" data-testid={`checklist-${item.id}`}>
                  <Icon size={16} className={`flex-shrink-0 mt-0.5 ${cfg.text}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-text-primary">{item.title}</p>
                    <p className="text-xs text-text-muted">{item.category} · {item.owner}</p>
                  </div>
                  <select
                    className={`text-xs font-bold px-2 py-1 rounded border-0 ${cfg.bg} ${cfg.text}`}
                    value={item.status}
                    onChange={(e) => updateStatus(item, e.target.value)}
                    data-testid={`checklist-status-${item.id}`}
                  >
                    <option value="not_started">Not Started</option>
                    <option value="in_progress">In Progress</option>
                    <option value="complete">Complete</option>
                    <option value="n_a">N/A</option>
                  </select>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}


// ─── MAIN PAGE ─────────────────────────────────────────────────────────────────
export default function ComplianceCenter() {
  const [tab, setTab] = useState('audit_log');

  return (
    <div className="space-y-6" data-testid="compliance-center">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-lg bg-brand/10 flex items-center justify-center text-brand flex-shrink-0">
          <Shield size={22} />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-text-primary">Compliance Center</h1>
          <p className="text-text-secondary mt-1">SOC 2 controls, evidence, and audit-log visibility for Ventas OS.</p>
        </div>
      </div>

      <div className="border-b border-border-subtle">
        <div className="flex gap-1" role="tablist">
          {TAB_KEYS.map(k => {
            const cfg = TAB_LABELS[k];
            const Icon = cfg.icon;
            const isActive = tab === k;
            return (
              <button
                key={k}
                role="tab"
                onClick={() => setTab(k)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px inline-flex items-center gap-1.5
                  ${isActive ? 'border-brand text-brand' : 'border-transparent text-text-muted hover:text-text-primary'}`}
                data-testid={`compliance-tab-${k}`}
              >
                <Icon size={14} /> {cfg.label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === 'audit_log' && <AuditLogTab />}
      {tab === 'vendors' && <VendorsTab />}
      {tab === 'access_review' && <AccessReviewTab />}
      {tab === 'checklist' && <ChecklistTab />}
    </div>
  );
}
